/**
 * @fileoverview Componente compartilhado de "Volume de mensagens" — usado pelo
 * SuperAdminPanel (visão cross-tenant) e pelo Dashboard de cada empresa
 * (escopado à empresa logada). Comportamento é controlado por props que
 * trocam o endpoint backend e escondem filtros/coluna de empresa.
 */

import React, { useState, useMemo, useEffect, Suspense, lazy } from "react";
import { Box, Typography, CircularProgress, Fade } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format, subDays } from "date-fns";

import api from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import FiltersToolbar from "./FiltersToolbar";
import MetricCard from "./MetricCard";
import ConnectionsTable from "./ConnectionsTable";
import { useMessageStats } from "./useMessageStats";

// Lazy: recharts é grande, só carrega quando dashboard renderiza pela 1ª vez.
const FlowChart = lazy(() => import("./FlowChart"));

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    section: {
      marginTop: theme.spacing(4),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    sectionHeader: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },
    sectionTitle: {
      fontSize: "1.15rem",
      fontWeight: 700,
      color: dark ? "#fff" : "#0f172a",
      letterSpacing: "-0.01em",
    },
    sectionSubtitle: {
      fontSize: "0.78rem",
      color: dark ? "rgba(255,255,255,0.42)" : "#94a3b8",
      letterSpacing: "0.02em",
    },
    metricsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: theme.spacing(2.5),
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94a3b8",
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(-0.5),
    },
    chartWrap: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(2.5),
    },
    loaderWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 80,
      color: dark ? "rgba(255,255,255,0.4)" : "#94a3b8",
      fontSize: 12,
    },
    error: {
      padding: 24,
      borderRadius: 14,
      background: dark ? "rgba(239,68,68,0.10)" : "rgba(239,68,68,0.05)",
      border: dark
        ? "1px solid rgba(239,68,68,0.28)"
        : "1px solid rgba(239,68,68,0.18)",
      color: dark ? "#fca5a5" : "#dc2626",
      fontSize: 13,
    },
  };
});

const today = () => format(new Date(), "yyyy-MM-dd");
const daysAgo = (n) => format(subDays(new Date(), n), "yyyy-MM-dd");

const MessageStats = ({
  endpoint = "/super-dashboard/messages-stats",
  refreshEndpoint = "/super-dashboard/messages-stats/refresh",
  connectionsEndpoint = "/super-dashboard/connections",
  companiesEndpoint = "/super-dashboard/companies",
  hideCompanyFilter = false,
  hideCompanyColumn = false,
  // Quando true, força que sempre haja ao menos uma conexão selecionada.
  // Auto-seleciona a primeira disponível no load e re-seleciona caso o filtro
  // fique vazio (impede visualizar agregado de várias conexões ao mesmo tempo).
  requireConnectionSelection = false,
  title = "Volume de mensagens",
  subtitle = "Não oficial + API Oficial · agregado a cada 3h · clique em atualizar para refresh manual",
  groupName,
}) => {
  const classes = useStyles();

  const [filters, setFilters] = useState({
    startDate: daysAgo(29),
    endDate: today(),
    companyIds: [],
    whatsappIds: [],
    provider: undefined,
  });

  const [companies, setCompanies] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  // Carrega listas de filtros 1×. Quando hideCompanyFilter está ativo, evita
  // o GET /companies (super-only) que retornaria 403 pra user comum.
  useEffect(() => {
    let cancelled = false;
    const requests = [api.get(connectionsEndpoint, { params: groupName ? { groupName } : {} })];
    if (!hideCompanyFilter) requests.unshift(api.get(companiesEndpoint));
    Promise.all(requests)
      .then((responses) => {
        if (cancelled) return;
        if (hideCompanyFilter) {
          const [wRes] = responses;
          const wList = Array.isArray(wRes.data) ? wRes.data : (wRes.data?.connections || []);
          setWhatsapps(wList);
        } else {
          const [cRes, wRes] = responses;
          setCompanies(cRes.data || []);
          const wList = Array.isArray(wRes.data) ? wRes.data : (wRes.data?.connections || []);
          setWhatsapps(wList);
        }
      })
      .catch(() => { /* silencioso — UI ainda funciona sem filtros */ });
    return () => { cancelled = true; };
  }, [hideCompanyFilter, companiesEndpoint, connectionsEndpoint, groupName]);

  // Modo escopado-por-conexão: sempre que houver conexões disponíveis e
  // nenhuma selecionada, força a primeira. Cobre tanto o load inicial quanto
  // o clique em "Limpar" no filtro de conexões.
  useEffect(() => {
    if (!requireConnectionSelection) return;
    if (!whatsapps?.length) return;
    if (filters.whatsappIds?.length > 0) return;
    const first = whatsapps[0];
    if (!first?.id) return;
    setFilters((prev) => ({ ...prev, whatsappIds: [first.id] }));
  }, [requireConnectionSelection, whatsapps, filters.whatsappIds]);

  // Debounce evita rajada de fetches enquanto usuário marca múltiplos itens
  // nos multi-selects (cada toggle dispararia um GET /messages-stats sem isso).
  const debouncedFilters = useDebounce(filters, 300);
  // Em modo escopado, segura o fetch até que haja uma conexão definida; evita
  // uma primeira request retornando agregado da empresa antes do auto-select.
  const queryEnabled = !requireConnectionSelection || (debouncedFilters.whatsappIds?.length > 0);
  const { data, isLoading, isFetching, isError, error, refresh } = useMessageStats(
    debouncedFilters,
    { endpoint, refreshEndpoint, includeCompanyIds: !hideCompanyFilter, enabled: queryEnabled, groupName }
  );

  const sparkData = useMemo(() => {
    const tl = data?.timeline;
    if (!tl?.length) return { sent: [], received: [], total: [], uniqueContacts: [], templatesSent: [] };
    return {
      sent: tl.map(t => (t.waha?.sent || 0) + (t["meta-api"]?.sent || 0)),
      received: tl.map(t => (t.waha?.received || 0) + (t["meta-api"]?.received || 0)),
      total: tl.map(t =>
        (t.waha?.sent || 0) + (t.waha?.received || 0) +
        (t["meta-api"]?.sent || 0) + (t["meta-api"]?.received || 0)
      ),
      uniqueContacts: tl.map(t => t.uniqueContacts || 0),
      templatesSent: tl.map(t => t.templatesSent || 0),
    };
  }, [data?.timeline]);

  // Dias do range REAL selecionado no filtro — usado no rodapé dos MetricCards
  // ("últimos N dias"). Antes mostrava sparkData.length, que conta só dias com
  // atividade — em um range de 90d sem msg em 86 deles virava "últimos 4 dias".
  const periodDays = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return 0;
    // Parse local (YYYY-MM-DD) — não usar new Date() direto, que interpreta
    // como UTC midnight e desloca dia em fusos negativos (vimos no DateRange).
    const [ys, ms, ds] = filters.startDate.split("-").map(Number);
    const [ye, me, de] = filters.endDate.split("-").map(Number);
    if (!ys || !ye) return 0;
    const start = new Date(ys, ms - 1, ds);
    const end = new Date(ye, me - 1, de);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(1, diff + 1); // inclusivo nos dois lados
  }, [filters.startDate, filters.endDate]);

  // Taxas derivadas. Denominador é sentCount (total de tentativas fromMe=true).
  // Sem envios no período → taxas zeradas pra evitar divisão por zero.
  const rates = useMemo(() => {
    const sent = data?.totals?.sent || 0;
    const read = data?.totals?.read || 0;
    const delivered = data?.totals?.delivered || 0;
    const failed = data?.totals?.failed || 0;
    const pct = (n) => (sent > 0 ? (n / sent) * 100 : 0);
    return {
      read: { rate: pct(read), count: read, total: sent },
      delivered: { rate: pct(delivered), count: delivered, total: sent },
      failed: { rate: pct(failed), count: failed, total: sent },
    };
  }, [data?.totals]);

  return (
    <Fade in timeout={400}>
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography component="div" className={classes.sectionTitle}>
              {title}
            </Typography>
            <Typography component="div" className={classes.sectionSubtitle}>
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <FiltersToolbar
          filters={filters}
          onChange={setFilters}
          companies={companies}
          whatsapps={whatsapps}
          lastUpdatedAt={data?.lastUpdatedAt}
          isFetching={isFetching}
          onRefresh={refresh}
          hideCompanyFilter={hideCompanyFilter}
        />

        {isError && (
          <Box className={classes.error}>
            Erro ao carregar estatísticas: {error?.response?.data?.error || error?.message || "desconhecido"}
          </Box>
        )}

        {/* react-query v3 reporta isLoading=true quando enabled=false
            (queryEnabled=false + data===undefined). Sem este guard, exibe
            spinner infinito "Carregando" em modo single-tenant esperando
            o user selecionar conexão. Quando queryEnabled=false trata como
            idle/empty, não como loading. */}
        {!queryEnabled && !data ? (
          <Box className={classes.loaderWrap} style={{ opacity: 0.6 }}>
            Selecione uma conexão acima para ver as estatísticas.
          </Box>
        ) : isLoading && !data ? (
          <Box className={classes.loaderWrap}>
            <CircularProgress size={20} style={{ marginRight: 10 }} />
            Carregando dashboard...
          </Box>
        ) : (
          <>
            <Box className={classes.metricsGrid}>
              <MetricCard
                label="Enviadas"
                value={data?.totals?.sent ?? 0}
                variant="sent"
                sparkData={sparkData.sent}
                periodDays={periodDays}
                delay={0}
              />
              <MetricCard
                label="Recebidas"
                value={data?.totals?.received ?? 0}
                variant="received"
                sparkData={sparkData.received}
                periodDays={periodDays}
                delay={50}
              />
              <MetricCard
                label="Total"
                value={data?.totals?.total ?? 0}
                variant="total"
                sparkData={sparkData.total}
                periodDays={periodDays}
                delay={100}
              />
              <MetricCard
                label="Conversas únicas"
                value={data?.totals?.uniqueContacts ?? 0}
                variant="unique"
                sparkData={sparkData.uniqueContacts}
                periodDays={periodDays}
                tooltip="Quantos clientes diferentes trocaram mensagens com a empresa no período. Cada cliente conta uma vez só, mesmo com muitas mensagens."
                delay={120}
              />
              <MetricCard
                label="Templates enviados"
                value={data?.totals?.templatesSent ?? 0}
                variant="templates"
                sparkData={sparkData.templatesSent}
                periodDays={periodDays}
                subtitle={
                  data?.templateOffenders?.length
                    ? `${data.templateOffenders.length} contato(s) receberam >1 template`
                    : undefined
                }
                tooltip={
                  "Quantas mensagens prontas (modelos aprovados do WhatsApp) foram enviadas no período. Um cliente pode receber mais de uma."
                }
                delay={140}
              />
            </Box>

            <Typography component="div" className={classes.sectionLabel}>
              Qualidade · {data?.totals?.sent
                ? `${(data.totals.sent).toLocaleString("pt-BR")} envios no período`
                : "sem envios no período"}
            </Typography>

            <Box className={classes.metricsGrid}>
              <MetricCard
                label="Taxa de leitura"
                value={rates.read.rate}
                variant="read"
                format="percent"
                subtitle={`${rates.read.count.toLocaleString("pt-BR")} de ${rates.read.total.toLocaleString("pt-BR")} lidas`}
                delay={150}
                tooltip="Quantas das mensagens enviadas foram lidas. Pode parecer menor do que é: quem desliga a confirmação de leitura (os dois tiques azuis) no WhatsApp não aparece como lido."
              />
              <MetricCard
                label="Taxa de entrega"
                value={rates.delivered.rate}
                variant="delivered"
                format="percent"
                subtitle={`${rates.delivered.count.toLocaleString("pt-BR")} de ${rates.delivered.total.toLocaleString("pt-BR")} entregues`}
                delay={200}
              />
              <MetricCard
                label="Taxa de falha"
                value={rates.failed.rate}
                variant="failed"
                format="percent"
                subtitle={`${rates.failed.count.toLocaleString("pt-BR")} de ${rates.failed.total.toLocaleString("pt-BR")} falharam`}
                delay={250}
              />
            </Box>

            <Box className={classes.chartWrap}>
              <Suspense fallback={
                <Box className={classes.loaderWrap}>
                  <CircularProgress size={18} style={{ marginRight: 8 }} />
                  Carregando gráfico...
                </Box>
              }>
                <FlowChart timeline={data?.timeline || []} />
              </Suspense>
              <ConnectionsTable rows={data?.byConnection || []} hideCompanyColumn={hideCompanyColumn} />
            </Box>
          </>
        )}
      </Box>
    </Fade>
  );
};

export default MessageStats;
